import { useEffect, useState, useCallback } from "react";
import {
  Breadcrumb,
  Button,
  message,
  Popconfirm,
  Skeleton,
  Space,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined } from "@ant-design/icons";
import AddCommission from "./AddCommission";
import EditCommission from "./EditCommission";
import { formatToNaira } from "../../../util/money";
import axiosConfig from "../../../util/axiosConfig";
import { CommissionsType } from "../../../shared/interfaces/commission.interface";
import { Link } from "react-router-dom";
import requestErrorHandler from "../../../util/requestErrorHandler";

const Commissions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [commissions, setCommissions] = useState<CommissionsType[]>([]);

  const columns: ColumnsType<CommissionsType> = [
    {
      title: "#",
      dataIndex: "id",
      render: (_, record, index) => index + 1,
    },
    {
      title: "Commission",
      dataIndex: "commissionName",
      key: "commissionName",
      render: (_, record) => (
        <Link to={"/admin/commissions/" + record.id}>
          {record.commissionName}
        </Link>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "General",
      dataIndex: "isGeneral",
      key: "isGeneral",
      render: (_, record) => (
        <div>{record.isGeneral === true ? "Yes" : "No"}</div>
      ),
    },
    {
      title: "Rate",
      dataIndex: "commissionRate",
      key: "commissionRate",
      render: (_, record) => (
        <div>{record.commissionRate ? record.commissionRate + "%" : "0%"}</div>
      ),
    },
    {
      title: "Processing Fee",
      dataIndex: "processingFee",
      key: "processingFee",
      render: (_, record) => (
        <div>
          {record.processingFee
            ? formatToNaira(record.processingFee)
            : formatToNaira(0)}
        </div>
      ),
    },
    {
      title: "Truck Owners",
      dataIndex: "isGeneral",
      key: "isGeneral",
      render: (_, record) => (
        <div>
          {record.isGeneral === true
            ? "All truck owners"
            : record.truckOwners
            ? record.truckOwners.length + " truck owners"
            : "None"}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record: CommissionsType) => (
        <Space size="middle">
          <EditCommission commission={record} getCommissions={getCommissions} />
          <Popconfirm
            title={`Are you sure you want to delete ${record.commissionName} commission?`}
            onConfirm={() => {
              handleDelete(record.id);
            }}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button type="primary" danger>
              <DeleteOutlined />
              <span className="">Delete</span>
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getCommissions = useCallback(async () => {
    setIsLoading(true);
    await axiosConfig
      .get("/commission/getall")
      .then((response: any) => {
        setCommissions(response.data.data);
      })
      .catch((error) => {
        requestErrorHandler(error);
      });

    setIsLoading(false);
  }, []);

  async function handleDelete(id: any) {
    await axiosConfig
      .delete(`/commission/delete?commissionId=${id}`)
      .then((response: any) => {
        message.success("Commission deleted successfully");
        getCommissions();
      })
      .catch((error) => {
        requestErrorHandler(error);
      });
  }

  useEffect(() => {
    getCommissions();
  }, [getCommissions]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="my-8">
          <h2 className="text-lg font-semibold">Commissions</h2>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={"/admin"}>Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Commissions</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        <div>
          <AddCommission getCommissions={getCommissions} />
        </div>
      </div>
      <div className="site-layout-background w-full overflow-auto bg-white">
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Table columns={columns} dataSource={commissions} />
        )}
      </div>
    </div>
  );
};

export default Commissions;
